import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { toast } from "react-hot-toast";
import {
  Gear,
  X,
  Check,
  Info,
  CurrencyDollar,
  Monitor,
  Rows,
  Sliders,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCountry } from "@/hooks/useCountry";
import { useSector } from "@/hooks/useSector";
import { useStage } from "@/hooks/useStage";
import { useRegion } from "@/hooks/useRegion";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  getInvestorPitchPreferences,
  saveInvestorPitchPreferences,
} from "@/services/api/VentureCapital";
import { Handshake } from "@phosphor-icons/react/dist/ssr";

function TagSelector({
  options,
  selectedIds,
  onToggle,
  placeholder = "Search...",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(
    () =>
      (options || []).filter((o) =>
        o.label.toLowerCase().includes(query.toLowerCase()),
      ),
    [options, query],
  );

  const selectedItems = useMemo(
    () => (options || []).filter((o) => selectedIds.includes(o.id)),
    [options, selectedIds],
  );

  return (
    <div ref={containerRef} className='space-y-2.5'>
      <div className='relative'>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className='w-full px-3 text-sm text-gray-800 bg-white border border-gray-300 rounded-lg h-9 focus:outline-none focus:border-orange-400 placeholder:text-gray-400'
        />

        {open && filtered.length > 0 && (
          <div className='absolute z-50 w-full mt-1 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg max-h-48'>
            {filtered.map((o) => {
              const isSelected = selectedIds.includes(o.id);
              return (
                <button
                  key={o.id}
                  type='button'
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onToggle(o.id);
                    inputRef.current?.focus();
                  }}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between gap-2 transition-colors ${
                    isSelected
                      ? "bg-orange-50 text-orange-700 hover:bg-orange-100"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className='flex-1 truncate'>{o.label}</span>
                  {isSelected && (
                    <Check
                      size={13}
                      weight='bold'
                      className='flex-shrink-0 text-orange-500'
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedItems.length > 0 && (
        <div className='flex flex-wrap gap-1.5'>
          {selectedItems.map((o) => (
            <span
              key={o.id}
              className='inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-orange-50 border border-orange-300 text-orange-700 font-medium'
            >
              {o.label}
              <button
                type='button'
                onClick={() => onToggle(o.id)}
                className='opacity-60 hover:opacity-100 leading-none ml-0.5'
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const defaultPrefs = {
  smart_filtering_mode: "ranking_only",
  revenue_preference: "agnostic",
  funding_pref_enabled: false,
  min_funding_raised_usd: "",
  max_funding_raised_usd: "",
  prefer_lead_secured: false,
  prefer_local_institutional_backing: false,
  support_female_founders: false,
  sectors_open_to_any: false,
  markets_open_to_any: false,
  stages_open_to_any: false,
  sector_ids: [],
  stage_ids: [],
  market_country_ids: [],
  market_region_ids: [],
};

export default function InvestorPreferencesModal({
  vcId,
  open,
  onOpenChange,
  onSaved,
}) {
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const isMobile = useIsMobile();

  const { countries } = useCountry({ searchStartupDropdown: true });
  const { data: sectors } = useSector({ searchStartupDropdown: true });
  const { data: stages } = useStage();
  const { regions } = useRegion({ searchStartupDropdown: true });

  const sectorOptions = useMemo(
    () => (sectors || []).map((s) => ({ id: Number(s.id), label: s.name })),
    [sectors],
  );
  const stageOptions = useMemo(
    () => (stages || []).map((s) => ({ id: Number(s.id), label: s.name })),
    [stages],
  );
  const regionOptions = useMemo(
    () => (regions || []).map((r) => ({ id: Number(r.id), label: r.name })),
    [regions],
  );
  const countryOptions = useMemo(
    () =>
      (countries || []).map((c) => ({
        id: Number(c.id),
        label: c.country_name,
      })),
    [countries],
  );

  const loadPreferences = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvestorPitchPreferences(vcId);
      const data = res.data?.data || {};
      const savedRegionIds = (data.regions || []).map((r) => Number(r.id));

      setPrefs({
        smart_filtering_mode: data.smart_filtering_mode || "ranking_only",
        revenue_preference: data.revenue_preference || "agnostic",
        funding_pref_enabled: data.funding_pref_enabled || false,
        min_funding_raised_usd: data.min_funding_raised_usd ?? "",
        max_funding_raised_usd: data.max_funding_raised_usd ?? "",
        prefer_lead_secured: data.prefer_lead_secured || false,
        prefer_local_institutional_backing:
          data.prefer_local_institutional_backing || false,
        support_female_founders: data.support_female_founders || false,
        sectors_open_to_any: data.sectors_open_to_any || false,
        markets_open_to_any:
          data.markets_open_to_any || data.is_global_market_preference || false,
        stages_open_to_any: data.stages_open_to_any || false,
        sector_ids: (data.sectors || []).map((s) => Number(s.id)),
        stage_ids: (data.stages || []).map((s) => Number(s.id)),
        market_country_ids: (data.countries || []).map((c) => Number(c.id)),
        market_region_ids: savedRegionIds,
      });
      setFieldErrors({});
    } catch (err) {
      console.error("Failed to load preferences:", err);
    } finally {
      setLoading(false);
    }
  }, [vcId]);

  useEffect(() => {
    if (open && vcId) loadPreferences();
  }, [open, vcId, loadPreferences]);

  const handleRegionToggle = async (regionId) => {
    const id = Number(regionId);
    const isSelected = prefs.market_region_ids.includes(id);

    setPrefs((prev) => ({
      ...prev,
      market_region_ids: isSelected
        ? prev.market_region_ids.filter((rid) => rid !== id)
        : [...prev.market_region_ids, id],
    }));
  };

  const handleSave = async () => {
    const nextErrors = {};

    if (prefs.funding_pref_enabled) {
      if (prefs.min_funding_raised_usd === "") {
        nextErrors.min_funding_raised_usd = ["Minimum funding is required."];
      }
      if (prefs.max_funding_raised_usd === "") {
        nextErrors.max_funding_raised_usd = ["Maximum funding is required."];
      }
      if (
        prefs.min_funding_raised_usd !== "" &&
        prefs.max_funding_raised_usd !== "" &&
        Number(prefs.min_funding_raised_usd) >
          Number(prefs.max_funding_raised_usd)
      ) {
        nextErrors.max_funding_raised_usd = [
          "Maximum funding must be greater than or equal to minimum funding.",
        ];
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setFieldErrors({});
    setSaving(true);
    try {
      const payload = {
        vc_id: vcId,
        smart_filtering_mode: prefs.smart_filtering_mode,
        revenue_preference: prefs.revenue_preference,
        funding_pref_enabled: prefs.funding_pref_enabled,
        prefer_lead_secured: prefs.prefer_lead_secured,
        prefer_local_institutional_backing:
          prefs.prefer_local_institutional_backing,
        support_female_founders: prefs.support_female_founders,
        is_global_market_preference: prefs.markets_open_to_any,
        sectors_open_to_any: prefs.sectors_open_to_any,
        markets_open_to_any: prefs.markets_open_to_any,
        stages_open_to_any: prefs.stages_open_to_any,
        sector_ids: prefs.sector_ids,
        stage_ids: prefs.stage_ids,
        market_country_ids: prefs.market_country_ids,
        market_region_ids: prefs.market_region_ids,
      };

      if (prefs.funding_pref_enabled) {
        if (prefs.min_funding_raised_usd !== "")
          payload.min_funding_raised_usd = Number(prefs.min_funding_raised_usd);
        if (prefs.max_funding_raised_usd !== "")
          payload.max_funding_raised_usd = Number(prefs.max_funding_raised_usd);
      }

      const r = await saveInvestorPitchPreferences(payload);
      toast.success(r.data?.message || "Preferences saved.");
      onOpenChange(false);
      if (onSaved) onSaved(prefs.smart_filtering_mode);
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        setFieldErrors(errors);
        toast.error("Please review the highlighted fields.");
      } else {
        toast.error(
          err.response?.data?.message || "Failed to save preferences.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleId = (field, id) => {
    setPrefs((prev) => {
      const arr = prev[field];
      return {
        ...prev,
        [field]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id],
      };
    });
  };

  const setField = (field, value) =>
    setPrefs((prev) => ({ ...prev, [field]: value }));

  const modeInfoText =
    prefs.smart_filtering_mode === "smart_pitch_bank"
      ? "Recommended → Immediate Email  ·  Other → Monthly Digest (1st of month)"
      : "All new pitches will trigger an immediate email notification.";

  const checkboxCls =
    "border-gray-400 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500 data-[state=checked]:text-white";

  const formBody = loading ? (
    <p className='py-8 text-sm text-center text-gray-400'>
      Loading preferences...
    </p>
  ) : (
    <>
      {/* ── PITCH BANK MODE ── */}
      <section>
        <div className='flex items-center gap-2 mb-3'>
          <Monitor size={14} weight='bold' className='text-orange-500' />
          <p className='text-xs font-semibold tracking-wide uppercase'>
            Pitch Bank Mode
          </p>
        </div>

        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
          {[
            {
              value: "ranking_only",
              title: "All Pitches (Ranked)",
              desc: "See every pitch, ranked by match. You'll receive email notifications for all new pitches.",
            },
            {
              value: "smart_pitch_bank",
              title: "Smart Pitch Bank",
              desc: "Pitches are auto-sorted into Recommended and Other. You'll only get immediate emails for Recommended pitches.",
            },
          ].map(({ value, title, desc }) => {
            const active = prefs.smart_filtering_mode === value;
            return (
              <button
                key={value}
                type='button'
                onClick={() => setField("smart_filtering_mode", value)}
                className={`relative text-left p-4 rounded-xl border transition-all ${
                  active
                    ? "border-orange-400 bg-orange-50 shadow-sm"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                {active && (
                  <span className='absolute w-2 h-2 bg-orange-500 rounded-full top-3 right-3' />
                )}
                <p
                  className={`text-sm font-semibold ${active ? "text-orange-600" : "text-gray-900"}`}
                >
                  {title}
                </p>
                <p className='mt-1 text-xs leading-normal opacity-50'>{desc}</p>
              </button>
            );
          })}
        </div>

        <div className='flex items-start gap-2 p-3 mt-3 text-xs border border-blue-200 rounded-lg bg-blue-50'>
          <Info size={13} className='flex-shrink-0 mt-0.5 text-blue-500' />
          <span className='translate-y-px'>{modeInfoText}</span>
        </div>
      </section>

      <div className='border-t border-gray-100' />

      {/* ── MATCHING CRITERIA ── */}
      <section>
        <div className='flex items-center gap-2 mb-1.5'>
          <Handshake size={14} weight='bold' className='text-orange-500' />
          <p className='text-xs font-semibold tracking-wide uppercase'>
            Matching Criteria
          </p>
        </div>
        <p className='mb-4 text-xs text-gray-500'>
          Leave any field empty to stay agnostic on that dimension. Only
          criteria you set affect scoring.
        </p>

        <div className='space-y-5'>
          {/* Sectors */}
          <div className='space-y-2.5'>
            <Label className='text-sm font-medium text-gray-800'>Sectors</Label>
            <TagSelector
              options={sectorOptions}
              selectedIds={prefs.sector_ids}
              onToggle={(id) => toggleId("sector_ids", id)}
              placeholder='Search sectors...'
            />
            <p className='text-xs text-gray-400'>
              {prefs.sectors_open_to_any
                ? "You'll still see pitches outside these, but pitches that match score higher."
                : "Only pitches matching these sectors earn full points on this criterion."}
            </p>
            <label className='flex items-center gap-2 !mt-4 text-xs text-gray-600 cursor-pointer select-none w-fit'>
              <Checkbox
                checked={prefs.sectors_open_to_any}
                onCheckedChange={(v) => setField("sectors_open_to_any", v)}
                className={checkboxCls}
              />
              Open to any sectors
            </label>
          </div>

          {/* Markets */}
          <div className='space-y-2.5'>
            <Label className='text-sm font-medium text-gray-800'>Markets</Label>

            <div className='space-y-1'>
              <p className='text-xs font-medium text-gray-500'>Region</p>
              <TagSelector
                options={regionOptions}
                selectedIds={prefs.market_region_ids}
                onToggle={handleRegionToggle}
                placeholder='Search regions...'
              />
            </div>

            <div className='space-y-1'>
              <p className='text-xs font-medium text-gray-500'>
                Country
              </p>
              <TagSelector
                options={countryOptions}
                selectedIds={prefs.market_country_ids}
                onToggle={(id) => toggleId("market_country_ids", id)}
                placeholder='Search countries...'
              />
            </div>

            <p className='text-xs text-gray-400'>
              {prefs.markets_open_to_any
                ? "You'll still see pitches outside these, but pitches that match score higher."
                : "Only pitches matching these markets earn full points on this criterion."}
            </p>
            <label className='flex items-center gap-2 !mt-4 text-xs text-gray-600 cursor-pointer select-none w-fit'>
              <Checkbox
                checked={prefs.markets_open_to_any}
                onCheckedChange={(v) => setField("markets_open_to_any", v)}
                className={checkboxCls}
              />
              Open to any markets
            </label>
          </div>

          {/* Stages */}
          <div className='space-y-2.5'>
            <Label className='text-sm font-medium text-gray-800'>Stages</Label>
            <TagSelector
              options={stageOptions}
              selectedIds={prefs.stage_ids}
              onToggle={(id) => toggleId("stage_ids", id)}
              placeholder='Search stages...'
            />
            <p className='text-xs text-gray-400'>
              {prefs.stages_open_to_any
                ? "You'll still see pitches outside these, but pitches that match score higher."
                : "Only pitches matching these stages earn full points on this criterion."}
            </p>
            <label className='flex items-center gap-2 !mt-4 text-xs text-gray-600 cursor-pointer select-none w-fit'>
              <Checkbox
                checked={prefs.stages_open_to_any}
                onCheckedChange={(v) => setField("stages_open_to_any", v)}
                className={checkboxCls}
              />
              Open to any stages
            </label>
          </div>
        </div>
      </section>

      <div className='border-t border-gray-100' />

      {/* ── REVENUE & FUNDING ── */}
      <section>
        <div className='flex items-center gap-2 mb-1.5'>
          <CurrencyDollar size={14} weight='bold' className='text-orange-500' />
          <p className='text-xs font-semibold tracking-wide uppercase'>
            Revenue &amp; Funding
          </p>
        </div>
        <p className='mb-4 text-xs text-gray-500'>
          Optional preferences that shape scoring. Neither filters pitches out.
        </p>

        <RadioGroup
          value={prefs.revenue_preference}
          onValueChange={(v) => setField("revenue_preference", v)}
          className='flex flex-wrap gap-2 mb-4'
        >
          {[
            { value: "agnostic", label: "Agnostic" },
            { value: "prefer_pre", label: "Pre-Revenue" },
            { value: "prefer_post", label: "Post-Revenue" },
          ].map(({ value, label }) => (
            <label
              key={value}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-xs font-medium ${
                prefs.revenue_preference === value
                  ? "border-orange-400 bg-orange-50 text-orange-700"
                  : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <RadioGroupItem value={value} className='sr-only' />
              {label}
            </label>
          ))}
        </RadioGroup>

        <div>
          <div className='flex items-start gap-3'>
            <Checkbox
              id='funding-filter'
              checked={prefs.funding_pref_enabled}
              onCheckedChange={(v) => setField("funding_pref_enabled", v)}
              className={`mt-0.5 flex-shrink-0 ${checkboxCls}`}
            />
            <div>
              <label
                htmlFor='funding-filter'
                className='text-sm font-medium text-gray-800 cursor-pointer'
              >
                Prefer startups that have raised funding before
              </label>
              <p className='text-xs text-gray-500 mt-0.5'>
                Toggle on to set a preferred funding range.
              </p>
            </div>
          </div>

          {prefs.funding_pref_enabled && (
            <div className='grid grid-cols-1 gap-3 mt-3 pl-7 sm:grid-cols-2'>
              <div>
                <Label className='block mb-1 text-xs text-gray-500'>
                  Min (USD)
                </Label>
                <Input
                  type='number'
                  placeholder='e.g. 100,000'
                  value={prefs.min_funding_raised_usd}
                  onChange={(e) =>
                    setField("min_funding_raised_usd", e.target.value)
                  }
                  className='text-sm text-gray-800 bg-white border-gray-300 h-9 focus:border-orange-400'
                />
                {fieldErrors.min_funding_raised_usd?.[0] && (
                  <p className='mt-1 text-xs text-red-500'>
                    {fieldErrors.min_funding_raised_usd[0]}
                  </p>
                )}
              </div>
              <div>
                <Label className='block mb-1 text-xs text-gray-500'>
                  Max (USD)
                </Label>
                <Input
                  type='number'
                  placeholder='e.g. 1,000,000'
                  value={prefs.max_funding_raised_usd}
                  onChange={(e) =>
                    setField("max_funding_raised_usd", e.target.value)
                  }
                  className='text-sm text-gray-800 bg-white border-gray-300 h-9 focus:border-orange-400'
                />
                {fieldErrors.max_funding_raised_usd?.[0] && (
                  <p className='mt-1 text-xs text-red-500'>
                    {fieldErrors.max_funding_raised_usd[0]}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <div className='border-t border-gray-100' />

      {/* ── ADDITIONAL PREFERENCES ── */}
      <section>
        <div className='flex items-center gap-2 mb-1.5'>
          <Sliders size={14} weight='bold' className='text-orange-500' />
          <p className='text-xs font-semibold tracking-wide uppercase'>
            Additional Preferences
          </p>
        </div>
        <p className='mb-4 text-xs text-gray-500'>
          Optional signals that nudge scoring without excluding any pitches.
        </p>

        <div className='space-y-4'>
          {[
            {
              field: "prefer_lead_secured",
              label: "Prefer startups with a lead investor secured",
              helper:
                "Boosts startups that have already secured a lead investor.",
            },
            {
              field: "prefer_local_institutional_backing",
              label: "Prefer startups with local institutional backing",
              helper:
                "Signals local validation in markets the startup is targeting.",
            },
            {
              field: "support_female_founders",
              label: "Actively support female founders",
              helper:
                "Adds a small boost to startups with female founders (never excludes others).",
            },
          ].map(({ field, label, helper }) => (
            <div key={field} className='flex items-start gap-3'>
              <Checkbox
                id={field}
                checked={prefs[field]}
                onCheckedChange={(v) => setField(field, v)}
                className={`mt-0.5 flex-shrink-0 ${checkboxCls}`}
              />
              <div>
                <label
                  htmlFor={field}
                  className='text-sm font-medium leading-snug text-gray-800 cursor-pointer'
                >
                  {label}
                </label>
                <p className='text-xs text-gray-500 mt-0.5'>{helper}</p>
              </div>
            </div>
          ))}
        </div>

        <div className='flex items-start gap-2 p-3 mt-5 text-xs text-gray-700 border border-blue-200 rounded-lg bg-blue-50'>
          <Info size={13} className='flex-shrink-0 mt-0.5 text-blue-500' />
          <span>
            Saving will rescore your recent pitches. No retroactive emails will
            be sent.
          </span>
        </div>
      </section>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          showHandle={false}
          className='bg-white text-gray-900 max-h-[92vh] flex flex-col rounded-t-2xl border-gray-200'
        >
          {/* Drag handle */}
          <div className='flex-shrink-0 w-10 h-1 mx-auto mt-3 mb-1 bg-gray-300 rounded-full' />

          <DrawerHeader className='flex-shrink-0 px-4 pt-2 pb-3 text-left border-b border-gray-200'>
            <DrawerTitle className='flex items-center gap-2 text-base text-gray-900'>
              <Gear size={18} className='text-orange-500' />
              Pitch Preferences
            </DrawerTitle>
            <p className='text-xs text-gray-500 mt-0.5'>
              Control how pitches are ranked and surfaced to you, including
              email notifications.
            </p>
          </DrawerHeader>

          <div className='flex-1 overflow-y-auto'>
            <div className='px-4 py-4 space-y-6'>{formBody}</div>
          </div>

          <DrawerFooter className='flex-row flex-shrink-0 gap-2 px-4 py-3 mt-0 bg-white border-t border-gray-200'>
            <DrawerClose asChild>
              <Button
                variant='outline'
                size='sm'
                disabled={saving}
                className='flex-1 text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              >
                Cancel
              </Button>
            </DrawerClose>
            <Button
              size='sm'
              onClick={handleSave}
              disabled={saving || loading}
              className='flex-1 text-white bg-gray-900 hover:bg-gray-800'
            >
              {saving ? "Saving..." : "Save Preferences"}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-full sm:max-w-xl bg-white text-background border-gray-200 max-h-[90vh] flex flex-col p-0 gap-y-0'>
        <DialogHeader className='px-4 pt-4 pb-4 border-b border-gray-200 sm:pt-5'>
          <DialogTitle className='flex items-center gap-2 text-base text-gray-900'>
            <Gear size={18} className='text-orange-500' />
            Pitch Preferences
          </DialogTitle>
          <p className='text-xs text-gray-500 mt-0.5'>
            Control how pitches are ranked and surfaced to you, including email
            notifications.
          </p>
        </DialogHeader>

        <ScrollArea className='flex-1 overflow-y-auto'>
          <div className='px-4 py-4 space-y-6 sm:py-5'>{formBody}</div>
        </ScrollArea>

        <DialogFooter className='gap-2 px-4 py-3 border-t border-gray-200 sm:py-4'>
          <DialogClose asChild>
            <Button
              variant='outline'
              size='sm'
              disabled={saving}
              className='flex-1 text-gray-700 bg-white border-gray-300 sm:flex-none hover:bg-gray-50 hover:border-gray-400'
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            size='sm'
            onClick={handleSave}
            disabled={saving || loading}
            className='flex-1 text-white bg-gray-900 sm:flex-none hover:bg-gray-800'
          >
            {saving ? "Saving..." : "Save Preferences"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
